function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { getBim360AdminUrl } from '@adsk/acs-sc-web-platform-api';
import { useGetAllPaginatedData, CONTEXT_NAMES } from '@adsk/alloy-helpers';
import { getOrCreateGlobalContext, useContextWithValidation } from '@adsk/alloy-react-context';
import { useCurrentAccountId } from './CurrentAccountIdContext';
import { fetchWithAccountType } from './helpers/fetchWithAccountType';
import { removeOldDuplicatesBy } from './helpers/removeOldDuplicatesBy';
import { useProviderOnlyOnce } from './hooks/useProviderOnlyOnce';

// See https://adminv1.api.b360-dev.autodesk.com/api#!/users/InternalV1_Users_get_project_entitlements

/**
 * Context for account entitlements.
 */
export const AccountEntitlementsContext = getOrCreateGlobalContext(CONTEXT_NAMES.AccountEntitlementsContext);
export const useAccountEntitlements = () => useContextWithValidation(AccountEntitlementsContext, {
  hook: 'useAccountEntitlements',
  provider: 'AccountEntitlementsProvider'
});

/**
 * This hook returns the current Account based on the current Account entitlements response.
 * Returns undefined if we don't have Account entitlement.
 *
 * @returns {undefined|object}
 */
export const useCurrentAccount = () => {
  const accountId = useCurrentAccountId();
  const {
    data: accountEntitlements
  } = useAccountEntitlements();
  return accountEntitlements === null || accountEntitlements === void 0 ? void 0 : accountEntitlements.accounts.find(account => (account === null || account === void 0 ? void 0 : account.account_id) === accountId);
};
const BASE_URL = `${getBim360AdminUrl()}/v1/account_entitlements?service_categories=acc,next_gen,classic,admin&include_services=true&limit=200&offset=0`;
const getNextKey = pageData => pageData.next ? `${getBim360AdminUrl()}/v1${pageData.next}` : '';
export const AccountEntitlementsProvider = _ref => {
  let {
    children,
    disabled
  } = _ref;
  const [data, setData] = useState(null);
  useProviderOnlyOnce({
    context: AccountEntitlementsContext,
    contextName: CONTEXT_NAMES.AccountEntitlementsContext
  });
  const {
    data: accountsPages,
    loading,
    error,
    retry
  } = useGetAllPaginatedData({
    url: !disabled ? BASE_URL : '',
    getNextKey,
    fetcher: fetchWithAccountType
  });
  const metadata = accountsPages[0];
  const accounts = useMemo(() => {
    return accountsPages && removeOldDuplicatesBy(accountsPages.flatMap(page => page.accounts), item => item.account_id);
  }, [accountsPages]);
  useEffect(() => {
    if (metadata) {
      setData(_objectSpread(_objectSpread({}, metadata), {}, {
        accounts,
        record_count: accounts.length
      }));
    }
  }, [accounts, metadata]);
  const value = useMemo(() => {
    const updateAccount = account => {
      const newData = _objectSpread({}, data);
      if (!newData.accounts) {
        newData.accounts = [];
      }
      newData.accounts = newData.accounts.map(p => p.account_id === account.account_id ? account : p);
      setData(newData);
    };
    const addAccount = account => {
      const newData = _objectSpread({}, data);
      newData.accounts = [account, ...newData.accounts];
      setData(newData);
    };
    return {
      data,
      loading,
      error,
      retry,
      setAccounts: setData,
      updateAccount,
      addAccount
    };
  }, [data, loading, error, retry]);
  return /*#__PURE__*/React.createElement(AccountEntitlementsContext.Provider, {
    value: value
  }, children);
};
AccountEntitlementsProvider.displayName = 'AccountEntitlementsProvider';
AccountEntitlementsProvider.propTypes = {
  /** Nodes with access to context */
  children: PropTypes.node,
  /** Whether we should disable this provider */
  disabled: PropTypes.bool
};
//# sourceMappingURL=AccountEntitlementsContext.js.map